import React, { useContext, type PropsWithChildren } from 'react';

export enum Products {
	jira = 'JIRA',
	confluence = 'CONFLUENCE',
	other = 'OTHER',
}

interface Props {
	products: Array<Products>;
	activeIndex: number;
	primaryProduct?: string; // To be populated if this component is used in anything other than: enum Products
}

const primaryProductIndex = 0;

const ProductContext = React.createContext<Props>({
	products: [],
	activeIndex: primaryProductIndex,
	primaryProduct: '',
});

type ProductProviderProps = PropsWithChildren<{
	products: Array<Products>;
	activeIndex?: number;
	primaryProduct?: string;
}>;

export const ProductProvider: ({
	children,
	products,
	activeIndex,
	primaryProduct,
}: ProductProviderProps) => React.JSX.Element = ({
	children,
	products,
	activeIndex = 0,
	primaryProduct = '',
}: ProductProviderProps) => (
	<ProductContext.Provider
		value={{
			products,
			activeIndex: Math.min(products.length - 1, Math.max(0, activeIndex)),
			primaryProduct,
		}}
	>
		{children}
	</ProductContext.Provider>
);

export const useProducts: () => Products[] = () => useContext(ProductContext).products;

// Returns primaryProduct if defined else the first product is considered the primary
export const usePrimaryProduct: () => string = () => {
	const { products, primaryProduct } = useContext(ProductContext);

	return primaryProduct || products[primaryProductIndex];
};

export const useActiveProduct: () => Products = () => {
	const context = useContext(ProductContext);
	return context.products[context.activeIndex];
};
